<template>
  <div>
    <Spinner v-if="isLoading" />
    <Message v-else-if="!!errorMessage.message" :message="errorMessage.message" :icon="errorMessage.icon" />
    <div v-else id="operation-table-full" :class="showTable ? 'card-item' : ''">
      <div v-if="$i18n.locale === 'pe'">
        <Message message="" icon="coming_soon_cl.svg" :as-card="true" />
      </div>
      <div v-else data-cy="operation-table">
        <b-field class="filter-fields">
          <div class="is-flex is-align-items-center is-justify-content-space-between is-flex-wrap-wrap-reverse">
            <b-dropdown
              v-if="showTable"
              data-cy="select-columns"
              :triggers="['click']"
              aria-role="list"
              scrollable
              :mobile-modal="false"
              :can-close="['escape', 'outside']"
              :close-on-click="false">
              <template #trigger="{ active }">
                <b-button :icon-right="active ? 'angle-up' : 'angle-down'" size="is-small">Seleccionar columnas</b-button>
              </template>
              <b-dropdown-item
                v-for="(column, index) in tableHeaders.filter(tableHeader => tableHeader.selectable)"
                :key="index"
                class="control">
                <b-checkbox v-model="column.visible" size="is-small"> {{ column.title }} </b-checkbox>
              </b-dropdown-item>
            </b-dropdown>
            <div class="is-flex second-group-fields is-justify-content-flex-end is-align-items-center" :class="!showTable ? 'no-table' : ''">
              <b-field class="m-0">
                <b-datepicker
                  id="datepicker-for-amortization-schedule"
                  v-model="date"
                  :locale="localeForDatePicker"
                  :placeholder="$t('dateForAmortizationSchedule')"
                  icon="calendar-alt"
                  size="is-small"
                  trap-focus
                  :mobile-modal="false"
                  :unselectable-dates="unselectableDates" />
              </b-field>
              <b-button
                data-cy="download-amortization-schedule"
                size="is-small"
                rounded
                @click="downloadAmortizationSchedule()">
                <i class="fa-solid fa-download has-text-danger" /> <span class="download-btn-text">{{ $t('csvDevelopmentTable') }}</span>
              </b-button>
            </div>
          </div>
        </b-field>
        <div
          v-if="$i18n.locale === 'br' && showTable"
          class="is-flex is-justify-content-space-between is-flex-wrap-wrap my-4">
          <b-tag
            class="my-1"
            type="is-light"
            icon="circle-info"
            data-cy="inflation-warning"
            rounded>
            {{ inflationWarning }}
          </b-tag>
          <b-tag
            v-if="missingPaymentsInformation"
            class="my-1"
            type="is-danger is-light"
            icon="triangle-exclamation"
            data-cy="resume-company-operation-id"
            rounded>
            {{ $t('missingPaymentsInformation') }}
          </b-tag>
        </div>
        <Spinner v-if="isLoadingNewDate && showTable" />
        <b-table
          v-if="!isLoadingNewDate && showTable"
          paginated
          hoverable
          :per-page="perPage"
          :current-page="currentPage"
          :data="table"
          :row-class="(row, index) => row.paymentOrigin === 's/i' && 'origin-other'"
          @page-change="$emit('update:currentPage', $event)">
          <b-table-column
            v-for="(column, index) in tableHeaders"
            :key="index"
            v-slot="props"
            :label="column.title"
            :visible="column.visible">
            <p v-if="column.field !== 'paymentOrigin' && column.field !=='isPaid'" :class="tableCellClasses(props.row, column.field)">
              {{ props.row[column.field] }}
            </p>
            <b-tooltip
              v-else
              :label="`${props.row[column.field]}${props.row.isBalanced ? '' : ` (desbalanceado ${props.row.calculatedTotalMonthlyPayment})`}${props.row.isBalanceConsistant
                ? '' : ` (inconsistente ${props.row.calculatedBalance})`}`"
              type="is-light"
              position="is-right">
              <i
                class="fa-solid fa-circle ml-4"
                :class="getClassForStatus(props.row[column.field], props.row.isBalanced, props.row.isBalanceConsistant)" />
            </b-tooltip>
          </b-table-column>
        </b-table>
      </div>
    </div>
  </div>
</template>
<script>
import Spinner from '@/ui/components/Spinner.vue';
import Message from '@/ui/components/Message.vue';
import { formatNumberByCountry } from '@/infrastructure/format';
import notification from '@/ui/mixins/notification';
import { getFeatureToggleByName } from '@/application/services/api-clients/features-toggles';

export default {
  name: 'AmortizationSchedule',
  components: { Message, Spinner },
  mixins: [notification],
  props: {
    isLoadingNewDate: { type: Boolean, requiered: true, default: false },
    operation: { type: Object, required: true, default: () => {} },
    dateForAmortizationSchedule: { type: Date, required: true, default: new Date() },
    client: { type: Object, required: true, default: () => {} },
    showTable: { type: Boolean, requiered: false, default: true }
  },
  data() {
    return {
      csv: null,
      currentPage: 1,
      errorMessage: { message: null, icon: null },
      isLoading: true,
      perPage: 10,
      tableHeaders: [],
      theoreticalPaymentsMap: null
    };
  },
  computed: {
    table() {
      const payments = this.operation.theoreticalPayments ? this.operation.theoreticalPayments : this.operation.payments;
      const table = payments.map(theoreticalPayment => {
        const tableLine = Object
          .entries(theoreticalPayment)
          .reduce((acc, [theoreticalPaymentKey, theoreticalPaymentValue]) => {
            const valueForLine = this.formatTableCell(theoreticalPaymentValue, theoreticalPaymentKey);
            acc[theoreticalPaymentKey] = valueForLine;
            return acc;
          }, {});
        return tableLine;
      });
      return table;
    },
    missingPaymentsInformation() {
      const payments = this.operation.theoreticalPayments ? this.operation.theoreticalPayments : this.operation.payments;
      return payments.some(payment => payment.paymentOrigin === 'OTHER');
    },
    date: {
      get() {
        return this.dateForAmortizationSchedule;
      },
      set(newValue) {
        this.$emit('new-date', newValue);
      }
    },
    localeForDatePicker() {
      return {
        br: 'pt-BR',
        cl: 'es-CL',
        pe: 'es-PE'
      }[this.$i18n.locale];
    },
    inflationWarning() {
      const { upperBoundBehaviour } = this.operation;
      if (upperBoundBehaviour === 'FIXED') {
        return this.$t('inflationWarningFixed');
      }
      if (upperBoundBehaviour === 'BOUND') {
        return this.$t('inflationWarningBound');
      }
      return 'TODO:nosé';
    }
  },
  async mounted() {
    try {
      await this.getFeatureToggleByName('amortization-schedule-table-fields');
      this.setTableHeaders();
      this.updateCurrentPageForAvailable('paymentOrigin');
    } catch {
      // Do nothing
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async getFeatureToggleByName(name) {
      try {
        this.theoreticalPaymentsMap = await getFeatureToggleByName(name);
      } catch (_error) {
        this.errorMessage = {
          message: 'Ocurrió un error al obtener las columnas de la tabla',
          icon: 'data_maintenance.svg'
        };
        throw _error; // Para interrumpir mounted
      }
    },
    setTableHeaders() {
      const payments = this.operation.theoreticalPayments || this.operation.payments;
      this.tableHeaders = payments[0]
        ? Object
          .keys(payments[0])
          .reduce((acc, fieldKey) => {
            const headerOnMap = this.theoreticalPaymentsMap.options.find(field => field.key === fieldKey);
            const newField = headerOnMap
              ? {
                title: headerOnMap.value.title || fieldKey,
                field: fieldKey,
                visible: headerOnMap.value.visible || false,
                selectable: typeof headerOnMap.value.selectable === 'boolean' ? headerOnMap.value.selectable : true,
                position: headerOnMap.value.position || 1000
              }
              : { title: fieldKey, field: fieldKey, visible: false, selectable: true, position: 1000 };
            acc.push(newField);
            return acc;
          }, [])
          .sort((a, b) => ((a.position || 1000) <= (b.position || 1000) ? -1 : 1))
        : [];
    },
    formatApprovedAmount(number) {
      const prefix = {
        br: 'R$ ',
        cl: 'UF ',
        pe: 'S/ '
      }[this.$i18n.locale];
      const formattedNumber = this.formatCurrency(number);
      return `${prefix}${formattedNumber}`;
    },
    formatCurrency(number) {
      const fractionDigits = {
        cl: ['MORTGAGE', 'PARKING'].includes(this.operation.type) ? 6 : 0,
        br: 2,
        pe: 2
      }[this.$i18n.locale];

      return formatNumberByCountry({
        number,
        countryIn: this.$i18n.locale,
        usePrefix: false,
        fractionDigits
      });
    },
    formatRow({ isGraceMonth, isFirstPayment }) {
      return {
        'is-grace-month': isGraceMonth,
        'is-first-payment': isFirstPayment
      };
    },
    tableRowClasses({ paymentOrigin }) {
      const tagClass = {};
      const paymentOriginClass = {
        OTHER: 'origin-other',
        TRANSACTION: 'origin-transaction',
        DEBT: 'origin-debt',
        THEORETICAL: 'origin-theoretical'
      }[paymentOrigin];
      if (typeof paymentOriginClass === 'string') {
        tagClass[`${paymentOriginClass}`] = true;
      }
    },
    tableCellClasses({ isGraceMonth, isFirstPayment, isPaid }, field) {
      const requiresSpecialClass = [
        'totalInsurances',
        'creditInsuranceExpenses',
        'creditInsurancePrize',
        'propertyInsurance',
        'lifeInsurance'
      ].some(v => field === v);
      const tagClass = {
        'is-grace-month': isGraceMonth && requiresSpecialClass,
        'is-first-payment': isFirstPayment && requiresSpecialClass
      };
      if (field === 'isPaid') {
        tagClass.tag = true;
        tagClass['med-size-text'] = true;
        tagClass[isPaid ? 'paid' : 'unpaid'] = true;
      }
      return tagClass;
    },
    formatTableCell(value, field) {
      const isNullishInflationaryUpperBound = field === 'inflationaryUpperBound' && value > 1;
      const isNullish = field !== 'isPaid' && value == null;
      if (isNullishInflationaryUpperBound || isNullish) {
        return '';
      }
      if (typeof value === 'number' && field !== 'paymentNumber') {
        return this.formatCurrency(value);
      }
      if (field === 'isPaid') {
        return this.$t(value ? 'paid' : 'unpaid');
      }
      if (field === 'paymentOrigin') {
        return {
          br: {
            OTHER: 's/i',
            TRANSACTION: 'pago',
            DEBT: 'disponible',
            THEORETICAL: 'futura'
          },
          cl: {
            OTHER: 's/i',
            TRANSACTION: 'pago',
            DEBT: 'disponible',
            THEORETICAL: 'futura'
          },
          pe: {
            OTHER: 's/i',
            TRANSACTION: 'pago',
            DEBT: 'disponível',
            THEORETICAL: 'futuro'
          }
        }[this.$i18n.locale][value];
      }
      return value;
    },
    async downloadAmortizationSchedule() {
      try {
        const lines = this.table.map(theoreticalPayment => {
          const line = this.tableHeaders.map(({ field }) => {
            const isNewValueAwful = theoreticalPayment[field] === undefined || !['string', 'boolean', 'number'].includes(typeof theoreticalPayment[field]);
            return isNewValueAwful ? 'null' : `"${theoreticalPayment[field]}"`;
          });
          return line.join(',');
        });
        const header = this.tableHeaders.map(tableHeader => tableHeader.title).join(',');
        const csv = `${header}\n${lines.join('\n')}`;
        this.csv = csv; // para testear

        const blobURL = URL.createObjectURL(new Blob([csv]));
        const a = document.createElement('a');
        a.style = 'display: none';
        a.download = `${this.date.toISOString().substring(0, 10)} - ${(this.client.name || '') + (this.client.lastname || '')} - ${this.operation.companyOperationId}.csv`;
        a.href = blobURL;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (_e) {
        this.alert({ message: 'Ocurrió un problema al descargar el archivo' });
      }
    },
    unselectableDates(day) {
      return false;
    },
    getClassForStatus(fieldValue, isBalanced, isBalanceConsistant) {
      if (!isBalanced || (!isBalanceConsistant && fieldValue !== 'futura' && fieldValue !== 'disponible' && fieldValue !== 's/i')) {
        return 'has-text-danger';
      }
      if (fieldValue === 's/i') {
        return 'has-text-grey-lighter';
      } if ((isBalanced && isBalanceConsistant && fieldValue === 'pago') || (isBalanced && isBalanceConsistant && fieldValue === 'Pagado')) {
        return 'has-text-primary';
      } if (fieldValue === 'disponible') {
        return 'has-text-warning';
      }
      return 'has-text-grey-light';
    },
    findFirstAvailable(columnField) {
      let availableIndex = -1;
      for (let i = 0; i < this.table.length; i += 1) {
        const row = this.table[i];
        if (row[columnField] === 'futura') {
          availableIndex = i - 1;
          break;
        }
      }
      return availableIndex;
    },
    updateCurrentPageForAvailable(columnField) {
      const index = this.findFirstAvailable(columnField);
      if (index !== -1) {
        const newPage = Math.ceil((index + 1) / this.perPage);
        this.currentPage = newPage;
        this.$emit('update:currentPage', this.currentPage);
      }
    }
  }
};
</script>

<style scoped>

  .grand-size-text {
    font-size: 1.5rem;
  }
  .big-size-text {
    font-size: 1.2rem;
  }
  .med-size-text {
    font-size: 0.9rem;
  }
  .card-item {
    width: auto;
    margin: 0 0 2rem 0;
    padding: 1% 1.5rem 2rem;
  }

  .filter-fields {
    margin-top: 0.5rem;
  }

  .filter-fields .is-flex {
    gap: 1rem;
  }

   .filter-fields .dropdown .button {
    width: 180px;
    border-radius: 4px;
  }

  .filter-fields .control , .filter-fields .dropdown-trigger .control {
    width: 180px;
  }

  .filter-fields .select.is-empty select {
    border-radius: 0px !important;
  }
  .second-group-fields.no-table {
      width: 100%;
    }

  @media (max-width: 679px) {
    .download-btn-text{
      display: none;
    }
    .second-group-fields {
      justify-content: space-between;
      width: 100%;
    }
    .filter-fields .dropdown {
      width: 100%;
    }
  }

  @media (max-width: 350px) {
    .grand-size-text {font-size: 1.2rem;}
    .big-size-text {font-size: 0.9rem;}
    .med-size-text {font-size: 0.75rem;}
  }
  .tag.paid {
  background-color: var(--primary);
  }

  .tag.unpaid {
    background-color: silver;
  }
  /* Sets grace month text color to red */
  p.is-grace-month {
    color: silver;
  }
  @media (max-width: 350px) {
    .grand-size-text {font-size: 1.2rem;}
    .big-size-text {font-size: 0.9rem;}
    .med-size-text {font-size: 0.75rem;}
  }
</style>
<style>
  .filter-fields .dropdown-trigger .control .input {
    border-radius: 4px;
  }
</style>

<i18n>
  {
    "br": {
      "dateForAmortizationSchedule": "Data para cálculos",
      "perPage": "por página",
      "csvDevelopmentTable": "Tabela de desenvolvimento",
      "inflationWarningFixed": "Valores não incluem IPCA e estão em Reais",
      "inflationWarningBound": "Os valores estão em reais. Operações com amortização inflacionária pelo sistema BOUND, IPCA de parcelas futuras foram calculados assumindo-os iguais ao CAP",
      "missingPaymentsInformation": "Esta operação não possui todas as informações históricas de pagamento.",
      "unpaid": "Não pago",
      "paid": "Pago"
    },
    "cl": {
      "dateForAmortizationSchedule": "Fecha para los cálculos",
      "perPage": "por página",
      "csvDevelopmentTable": "Tabla de desarrollo",
      "inflationWarningFixed": "",
      "inflationWarningBound": "Valores están en reales. Operaciones con amortización de la inflación bajo el sistema BOUND, IPCA de las cuotas futuras fueron calculados suponiendolas igual al CAP",
      "missingPaymentsInformation": "Las líneas marcadas en rojo no cuentan con información de pago en el producto",
      "unpaid": "No pagado",
      "paid": "Pagado"
    },
    "pe": {
      "dateForAmortizationSchedule": "Fecha para los cálculos",
      "perPage": "por página",
      "csvDevelopmentTable": "Tabla de desarrollo",
      "inflationWarningFixed": "",
      "inflationWarningBound": "",
      "missingPaymentsInformation": "Las líneas marcadas en rojo no cuentan con información de pago en el producto",
      "unpaid": "No pagado",
      "paid": "Pagado"
    }
  }
</i18n>
